import React, { useState, useEffect } from 'react';
import { usageBarColor } from '@/lib/utils';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { ArrowLeft, Edit, Plus, Infinity, Upload, SlidersHorizontal, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

export default function SponsorDetailPage() {
  const { t } = useTranslation();
  const { hasPerm } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();
  const [sponsor, setSponsor] = useState(null);
  const [rights, setRights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [adjustTarget, setAdjustTarget] = useState(null);
  const [adjustForm, setAdjustForm] = useState({ adjustment: '', reason: '' });
  const [editForm, setEditForm] = useState({});
  const [logoFile, setLogoFile] = useState(null);
  const [assignForm, setAssignForm] = useState({ right_id: '', allocation_type: 'limited', allocated_amount: '', per_match_limit: '', notes: '' });
  const [managers, setManagers] = useState([]);

  const load = async () => {
    try {
      const [s, r, m] = await Promise.all([api.get(`/api/sponsors/${id}`), api.get('/api/rights'), api.get('/api/account-managers')]);
      setSponsor(s.data);
      setRights(r.data.filter(rt => rt.status === 'active'));
      setManagers(m.data);
      setEditForm({ company_name: s.data.company_name, status: s.data.status, notes: s.data.notes || '', account_manager_ids: s.data.account_manager_ids || [] });
    } catch { toast.error('Failed to load sponsor'); }
    setLoading(false);
  };

  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleEdit = async () => {
    if (!editForm.company_name?.trim()) { toast.error('Company name is required'); return; }
    try {
      await api.put(`/api/sponsors/${id}`, editForm);
      // Upload logo if selected
      if (logoFile) {
        const fd = new FormData();
        fd.append('file', logoFile);
        try {
          await api.post(`/api/sponsors/${id}/logo`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        } catch (logoErr) { toast.error(logoErr.response?.data?.detail || 'Logo upload failed'); }
        setLogoFile(null);
      }
      toast.success(t('sponsor_updated'));
      setEditOpen(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Update failed'); }
  };

  const handleAssign = async () => {
    if (!assignForm.right_id) { toast.error(t('select_right')); return; }
    const body = { sponsor_id: id, right_id: assignForm.right_id, allocation_type: assignForm.allocation_type,
      allocated_amount: assignForm.allocation_type === 'limited' ? parseInt(assignForm.allocated_amount) || 0 : null,
      per_match_limit: assignForm.per_match_limit ? parseInt(assignForm.per_match_limit) : null, notes: assignForm.notes };
    try { await api.post('/api/assignments', body); toast.success(t('assign_right')); setAssignOpen(false); setAssignForm({ right_id: '', allocation_type: 'limited', allocated_amount: '', per_match_limit: '', notes: '' }); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Assign failed'); }
  };

  const handleDeleteSponsor = async () => {
    if (!window.confirm(`Delete "${sponsor.company_name}" permanently? This will also delete all their requests, rights, and assignments. This cannot be undone.`)) return;
    try {
      await api.delete(`/api/sponsors/${id}`);
      toast.success(`Sponsor "${sponsor.company_name}" deleted`);
      navigate('/sponsors');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete'); }
  };

  const openAdjust = (a) => {
    setAdjustTarget(a);
    setAdjustForm({ adjustment: '', reason: '' });
    setAdjustOpen(true);
  };

  const handleAdjust = async () => {
    const adj = parseInt(adjustForm.adjustment);
    if (!adj || adj === 0) { toast.error(t('enter_valid_adjustment')); return; }
    if (!adjustForm.reason || !adjustForm.reason.trim()) { toast.error(t('reason_required') || 'Reason is required'); return; }
    try {
      await api.post(`/api/assignments/${adjustTarget.id}/adjust`, { adjustment: adj, reason: adjustForm.reason });
      toast.success(t('allocation_adjusted'));
      setAdjustOpen(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Adjustment failed'); }
  };

  if (loading) return <div className="flex items-center justify-center h-64 text-gray-400 text-base">{t('loading')}</div>;
  if (!sponsor) return <div className="text-center py-20 text-gray-400 text-base">Sponsor not found</div>;

  const statusColor = { submitted: 'bg-blue-100 text-blue-700', approved: 'bg-green-100 text-green-700', partially_approved: 'bg-yellow-100 text-yellow-700', rejected: 'bg-red-100 text-red-700', cancelled: 'bg-gray-100 text-gray-600', under_review: 'bg-amber-100 text-amber-700' };
  const assignedRightIds = (sponsor.assignments || []).map(a => a.right_id);
  const availableRights = rights.filter(r => !assignedRightIds.includes(r.id));

  return (
    <div data-testid="sponsor-detail-page" className="space-y-6 animate-fade-in">
      <div className="flex items-center gap-4">
        <Link to="/sponsors"><Button variant="ghost" size="sm" className="text-base"><ArrowLeft size={16} className="mr-1" /> {t('back')}</Button></Link>
        <div className="flex items-center gap-3">
          {sponsor.logo_url ? (
            <img src={sponsor.logo_url} alt={sponsor.company_name} className="h-12 w-12 rounded-xl object-cover border border-gray-200" />
          ) : (
            <div className="h-12 w-12 rounded-xl bg-gray-100 flex items-center justify-center text-lg font-bold text-gray-400">{sponsor.company_name?.[0]}</div>
          )}
          <h1 className="text-xl font-semibold text-gray-900">{sponsor.company_name}</h1>
        </div>
        <Badge className={`pointer-events-none select-none ${sponsor.status === 'active' ? 'bg-green-100 text-green-700 text-base' : 'bg-red-100 text-red-700 text-base'}`}>{sponsor.status}</Badge>
        <div className="ml-auto">
          {hasPerm('edit_sponsors') && (
            <Button
              data-testid="delete-sponsor-btn"
              variant="outline"
              size="sm"
              onClick={handleDeleteSponsor}
              className="border-red-200 text-red-500 hover:bg-red-50 hover:text-red-700 text-xs h-8"
            >
              <Trash2 size={13} className="mr-1" /> Delete
            </Button>
          )}
        </div>
      </div>

      <Tabs defaultValue="info" className="w-full">
        <TabsList className="bg-gray-100 border-gray-200">
          <TabsTrigger value="info" className="text-base">{t('info')}</TabsTrigger>
          <TabsTrigger value="rights" className="text-base">{t('rights')} ({sponsor.assignments?.length || 0})</TabsTrigger>
          <TabsTrigger value="requests" className="text-base">{t('requests')} ({(sponsor.requests || []).filter(r => r.request_status !== 'cancelled').length})</TabsTrigger>
        </TabsList>

        <TabsContent value="info" className="mt-4">
          <Card className="bg-white border-gray-200/80 shadow-none">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-sm font-medium text-gray-700">{t('sponsor_details')}</CardTitle>
              <div className="flex items-center gap-2">
                {hasPerm('edit_sponsors') && (
                <Dialog open={editOpen} onOpenChange={setEditOpen}>
                  <DialogTrigger asChild><Button data-testid="edit-sponsor-btn" variant="outline" size="sm" className="border-gray-300 text-base"><Edit size={14} className="mr-1" /> {t('edit')}</Button></DialogTrigger>
                  <DialogContent className="bg-white border-gray-200 max-h-[90vh] overflow-y-auto">
                    <DialogHeader><DialogTitle className="text-lg font-semibold text-gray-900">{t('edit')} {t('sponsor')}</DialogTitle></DialogHeader>
                    <div className="space-y-4 mt-4">
                      <div><Label className="text-base text-gray-700">{t('company_name')} *</Label><Input value={editForm.company_name || ''} onChange={e => setEditForm(p => ({...p, company_name: e.target.value}))} className="bg-gray-50 border-gray-300 h-11 text-base" /></div>
                      <div>
                        <Label className="text-base text-gray-700">{t('sponsor_logo') || 'Logo'}</Label>
                        <div className="flex items-center gap-3 mt-1">
                          {(logoFile || sponsor?.logo_url) && (
                            <img src={logoFile ? URL.createObjectURL(logoFile) : sponsor?.logo_url} alt="Logo" className="h-12 w-12 rounded-lg object-cover border border-gray-200" />
                          )}
                          <div>
                            <label className="cursor-pointer flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-300 bg-gray-50 text-sm text-gray-500 hover:border-primary hover:text-primary transition-colors">
                              <Upload size={14} /> {t('upload_logo') || 'Upload Logo'}
                              <input type="file" accept="image/*" className="hidden" onChange={e => setLogoFile(e.target.files[0])} />
                            </label>
                            <p className="text-[10px] text-gray-400 mt-0.5">Max 2MB · 300×300px · PNG, JPG, SVG</p>
                          </div>
                        </div>
                      </div>
                      <div>
                        <Label className="text-base text-gray-700">{t('account_manager')}</Label>
                        <div className="mt-1 space-y-2">
                          <div className="flex flex-wrap gap-1.5 min-h-[28px]">
                            {(editForm.account_manager_ids || []).map(mid => {
                              const mgr = managers.find(m => m.id === mid);
                              const name = mgr?.name || sponsor?.account_manager_names?.find((_, i) => (sponsor.account_manager_ids || [])[i] === mid) || mid.slice(0, 8);
                              return (
                                <Badge key={mid} variant="outline" className={`text-xs pr-1 flex items-center gap-1 ${mgr ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-amber-50 text-amber-700 border-amber-200'}`}>
                                  {name}{!mgr && ' (non-manager)'}
                                  <button type="button" onClick={() => setEditForm(p => ({...p, account_manager_ids: p.account_manager_ids.filter(i => i !== mid)}))} className="ml-0.5 hover:text-red-500">x</button>
                                </Badge>
                              );
                            })}
                          </div>
                          <Select value="" onValueChange={v => { if (v && !(editForm.account_manager_ids || []).includes(v)) setEditForm(p => ({...p, account_manager_ids: [...(p.account_manager_ids || []), v]})); }}>
                            <SelectTrigger className="bg-gray-50 border-gray-300 h-11 text-base"><SelectValue placeholder={t('select_manager')} /></SelectTrigger>
                            <SelectContent className="bg-white border-gray-200">{managers.filter(m => !(editForm.account_manager_ids || []).includes(m.id)).map(m => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div><Label className="text-base text-gray-700">{t('status')}</Label>
                        <Select value={editForm.status} onValueChange={v => setEditForm(p => ({...p, status: v}))}>
                          <SelectTrigger className="bg-gray-50 border-gray-300 h-11 text-base"><SelectValue /></SelectTrigger>
                          <SelectContent className="bg-white border-gray-200"><SelectItem value="active">Active</SelectItem><SelectItem value="inactive">Inactive</SelectItem><SelectItem value="archived">Archived</SelectItem></SelectContent>
                        </Select>
                      </div>
                      <div><Label className="text-base text-gray-700">{t('notes')}</Label><Textarea value={editForm.notes || ''} onChange={e => setEditForm(p => ({...p, notes: e.target.value}))} className="bg-gray-50 border-gray-300 text-base" /></div>
                      <Button onClick={handleEdit} className="w-full bg-gray-900 hover:bg-gray-800 text-sm h-10">{t('save_changes')}</Button>
                    </div>
                  </DialogContent>
                </Dialog>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex items-start gap-6 mb-4">
                {sponsor.logo_url && (
                  <img src={sponsor.logo_url} alt={sponsor.company_name} className="h-20 w-20 rounded-xl object-cover border border-gray-200" />
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-base flex-1">
                  <div><span className="text-gray-500">{t('contact')}:</span> <span className="ml-2 text-gray-800 font-medium">{sponsor.contact_name}</span></div>
                  <div><span className="text-gray-500">{t('email')}:</span> <span className="ml-2 text-gray-800 font-medium">{sponsor.email}</span></div>
                  <div><span className="text-gray-500">{t('mobile')}:</span> <span className="ml-2 text-gray-800 font-medium">{sponsor.mobile || '---'}</span></div>
                  <div><span className="text-gray-500">{t('account_manager')}:</span> <span className="ml-2 text-gray-800 font-medium">{sponsor.account_manager_names?.length > 0 ? sponsor.account_manager_names.join(', ') : sponsor.account_manager_name}</span></div>
                  <div><span className="text-gray-500">{t('contract_start')}:</span> <span className="ml-2 text-gray-800 font-medium">{sponsor.contract_start || '---'} to {sponsor.contract_end || '---'}</span></div>
                  <div><span className="text-gray-500">{t('notes')}:</span> <span className="ml-2 text-gray-800">{sponsor.notes || '---'}</span></div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="rights" className="mt-4 space-y-4">
          <div className="flex justify-end">
            {hasPerm('edit_rights') && (
            <Dialog open={assignOpen} onOpenChange={setAssignOpen}>
              <DialogTrigger asChild><Button data-testid="assign-right-btn" className="bg-gray-900 hover:bg-gray-800 text-sm"><Plus size={18} className="mr-2" /> {t('assign_right')}</Button></DialogTrigger>
              <DialogContent className="bg-white border-gray-200">
                <DialogHeader><DialogTitle className="text-lg font-semibold text-gray-900">{t('assign_right')}</DialogTitle></DialogHeader>
                <div className="space-y-4 mt-4">
                  <div><Label className="text-base text-gray-700">{t('right')} *</Label>
                    <Select value={assignForm.right_id} onValueChange={v => setAssignForm(p => ({...p, right_id: v}))}>
                      <SelectTrigger className="bg-gray-50 border-gray-300 h-11 text-base"><SelectValue placeholder={t('select_right')} /></SelectTrigger>
                      <SelectContent className="bg-white border-gray-200">{availableRights.map(r => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label className="text-base text-gray-700">{t('allocation_type')}</Label>
                    <Select value={assignForm.allocation_type} onValueChange={v => setAssignForm(p => ({...p, allocation_type: v}))}>
                      <SelectTrigger className="bg-gray-50 border-gray-300 h-11 text-base"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-white border-gray-200"><SelectItem value="limited">{t('limited')}</SelectItem><SelectItem value="unlimited">{t('unlimited')}</SelectItem></SelectContent>
                    </Select>
                  </div>
                  {assignForm.allocation_type === 'limited' && <div><Label className="text-base text-gray-700">{t('allocated_amount')}</Label><Input data-testid="alloc-amount-input" type="number" value={assignForm.allocated_amount} onChange={e => setAssignForm(p => ({...p, allocated_amount: e.target.value}))} className="bg-gray-50 border-gray-300 h-11 text-base" /></div>}
                  <div><Label className="text-base text-gray-700">{t('per_match_limit')}</Label><Input type="number" value={assignForm.per_match_limit} onChange={e => setAssignForm(p => ({...p, per_match_limit: e.target.value}))} className="bg-gray-50 border-gray-300 h-11 text-base" /></div>
                  <div><Label className="text-base text-gray-700">{t('notes')}</Label><Textarea value={assignForm.notes} onChange={e => setAssignForm(p => ({...p, notes: e.target.value}))} className="bg-gray-50 border-gray-300 text-base" /></div>
                  <Button data-testid="save-assignment-btn" onClick={handleAssign} className="w-full bg-gray-900 hover:bg-gray-800 text-sm h-10">{t('assign_right')}</Button>
                </div>
              </DialogContent>
            </Dialog>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {(sponsor.assignments || []).map(a => {
              const isU = a.allocation_type === 'unlimited';
              const pct = isU ? 100 : a.allocated_amount ? Math.round(((a.used_amount || 0) / a.allocated_amount) * 100) : 0;
              return (
                <Card key={a.id} className="bg-white border-gray-200/80 shadow-none hover:shadow-md transition-shadow">
                  <CardContent className="pt-5 pb-4 px-5">
                    <div className="flex items-start justify-between mb-3">
                      <p className="text-base font-semibold text-gray-800">{a.right_name}</p>
                      <div className="flex items-center gap-1.5">
                        {!isU && hasPerm('adjust_allocation') && (
                          <Button data-testid={`adjust-btn-${a.id}`} variant="ghost" size="sm" onClick={() => openAdjust(a)} className="h-7 px-2 text-gray-500 hover:text-blue-600">
                            <SlidersHorizontal size={14} />
                          </Button>
                        )}
                        <Badge className={`pointer-events-none select-none ${isU ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700'}`}>{isU ? t('unlimited') : t('limited')}</Badge>
                      </div>
                    </div>
                    {isU ? <div className="space-y-2"><div className="flex items-center gap-2 text-emerald-600"><Infinity size={18} /><span className="text-base font-medium">{t('unlimited')}</span></div><div className="text-sm text-gray-600">{t('used')}: <span className="font-semibold text-amber-600">{a.used_amount || 0}</span></div></div> : (
                      <div className="space-y-3">
                        <div>
                          <div className="flex justify-between text-xs text-gray-400 mb-1">
                            <span>{a.used_amount || 0} / {a.allocated_amount} {t('used').toLowerCase()}</span>
                            <span className="tabular-nums">{pct}%</span>
                          </div>
                          <div data-testid={`usage-bar-${a.id}`} className="w-full h-2 bg-gray-100 dark:bg-[var(--chip)] rounded-full overflow-hidden">
                            <div className={`h-full rounded-full transition-all ${usageBarColor(pct)}`} style={{ width: `${Math.min(100, pct)}%` }} />
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-1 text-sm text-gray-600">
                          <div>{t('alloc')}: <span className="font-semibold text-gray-800">{a.allocated_amount}</span></div>
                          <div>{t('used')}: <span className="font-semibold text-amber-600">{a.used_amount || 0}</span></div>
                          <div>{t('remain')}: <span className="font-semibold text-blue-600">{a.remaining_amount}</span></div>
                          {a.per_match_limit && <div>{t('per_match')}: <span className="font-semibold">{a.per_match_limit}</span></div>}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <Dialog open={adjustOpen} onOpenChange={setAdjustOpen}>
            <DialogContent className="bg-white border-gray-200 max-w-sm">
              <DialogHeader><DialogTitle className="text-lg font-semibold text-gray-900">{t('adjust_allocation')}</DialogTitle></DialogHeader>
              {adjustTarget && (
                <div className="space-y-4 mt-2">
                  <div className="bg-gray-50 rounded-lg p-3 text-sm">
                    <p className="font-medium text-gray-800">{adjustTarget.right_name}</p>
                    <p className="text-gray-500 mt-1">{t('current_allocation')}: <span className="font-semibold text-gray-800">{adjustTarget.allocated_amount}</span> | {t('remain')}: <span className="font-semibold text-blue-600">{adjustTarget.remaining_amount}</span></p>
                  </div>
                  <div>
                    <Label className="text-sm text-gray-700">{t('adjustment_amount')}</Label>
                    <p className="text-xs text-gray-400 mb-1">{t('adjustment_hint')}</p>
                    <Input data-testid="adjust-amount-input" type="number" value={adjustForm.adjustment} onChange={e => setAdjustForm(p => ({...p, adjustment: e.target.value}))} placeholder="e.g. +10 or -5" className="bg-gray-50 border-gray-300 h-10 text-sm" />
                  </div>
                  <div>
                    <Label className="text-sm text-gray-700">{t('reason')} *</Label>
                    <Input data-testid="adjust-reason-input" value={adjustForm.reason} onChange={e => setAdjustForm(p => ({...p, reason: e.target.value}))} placeholder={t('adjustment_reason_placeholder')} className="bg-gray-50 border-gray-300 h-10 text-sm" required />
                  </div>
                  <Button data-testid="save-adjust-btn" onClick={handleAdjust} className="w-full bg-gray-900 hover:bg-gray-800 text-sm h-9">{t('apply_adjustment')}</Button>
                </div>
              )}
            </DialogContent>
          </Dialog>
        </TabsContent>

        <TabsContent value="requests" className="mt-4">
          <Card className="bg-white border-gray-200/80 shadow-none">
            <CardContent className="p-0">
              <Table>
                <TableHeader><TableRow className="border-gray-200 hover:bg-transparent">
                  <TableHead className="text-xs text-gray-400 font-medium">{t('match')}</TableHead>
                  <TableHead className="text-xs text-gray-400 font-medium">{t('items')}</TableHead>
                  <TableHead className="text-xs text-gray-400 font-medium">{t('status')}</TableHead>
                  <TableHead className="text-xs text-gray-400 font-medium">{t('date')}</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {(sponsor.requests || []).length === 0 ? (
                    <TableRow><TableCell colSpan={4} className="text-center py-8 text-gray-400 text-base">{t('no_requests_found')}</TableCell></TableRow>
                  ) : (sponsor.requests || []).map(r => (
                    <TableRow key={r.id} className="border-gray-100 hover:bg-gray-50">
                      <TableCell><Link to={`/requests/${r.id}`} className="text-primary hover:underline text-base">{r.match_title}</Link></TableCell>
                      <TableCell className="text-base text-gray-700">{r.items?.length || 0} {t('items')}</TableCell>
                      <TableCell><Badge className={`pointer-events-none select-none ${statusColor[r.request_status] || 'bg-gray-100 text-gray-600'}`}>{r.request_status?.replace('_',' ')}</Badge></TableCell>
                      <TableCell className="text-gray-500 text-sm">{r.created_at?.split('T')[0]}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
